/**
 * "Beat the Monkey" (specs/home-page AC-10b): the gold button beside Send to a Friend, with the shared
 * AnimatedMonkey rig leaning out of its bottom-right corner and popping every few seconds the way the
 * design's `zpMonkeyPop` does (wind-up squash, leap, landing squash, pivot at the feet).
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { View, Text, Pressable, Platform, Animated, Easing } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useReducedMotion } from '../../../src/shared/hooks/useReducedMotion';
import type { Theme } from '../../../src/theme/getTheme';
import { HOME_COPY, HOME_TEST_IDS } from '../../../src/home/constants/home.constants';
import { AnimatedMonkey } from '../../onboarding/AnimatedMonkey/AnimatedMonkey';
import { SLATE_MONKEY_SIZE, type TodayChallengeCardStyles } from './TodayChallengeCard.styles';
import { MONKEY_POP_KEYFRAMES, MONKEY_POP_REST_MS, MONKEY_POP_SEGMENT_MS } from './BeatTheMonkeyButton.constants';

interface MonkeyPop {
  translateY: Animated.AnimatedInterpolation<number>;
  scaleX: Animated.AnimatedInterpolation<number>;
  scaleY: Animated.AnimatedInterpolation<number>;
}

const POP_INPUT_RANGE = MONKEY_POP_KEYFRAMES.map((_, i) => i);
const POP_EASING = Easing.inOut(Easing.ease);

/**
 * One `phase` value walks the keyframe stops (0 → N-1), one eased timing per segment, so translate and
 * both scales stay in lock-step exactly as CSS keyframes do; then it snaps back and rests.
 */
function useMonkeyPop(enabled: boolean): MonkeyPop {
  const phase = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!enabled) {
      phase.setValue(0);
      return;
    }
    const hop = MONKEY_POP_SEGMENT_MS.map((duration, i) =>
      Animated.timing(phase, { toValue: i + 1, duration, easing: POP_EASING, useNativeDriver: true }),
    );
    const loop = Animated.loop(
      Animated.sequence([
        Animated.delay(MONKEY_POP_REST_MS),
        ...hop,
        Animated.timing(phase, { toValue: 0, duration: 0, useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [enabled, phase]);

  return useMemo(
    () => ({
      translateY: phase.interpolate({ inputRange: POP_INPUT_RANGE, outputRange: MONKEY_POP_KEYFRAMES.map((k) => k.translateY) }),
      scaleX: phase.interpolate({ inputRange: POP_INPUT_RANGE, outputRange: MONKEY_POP_KEYFRAMES.map((k) => k.scaleX) }),
      scaleY: phase.interpolate({ inputRange: POP_INPUT_RANGE, outputRange: MONKEY_POP_KEYFRAMES.map((k) => k.scaleY) }),
    }),
    [phase],
  );
}

export interface BeatTheMonkeyButtonProps {
  onPress: () => void;
  styles: TodayChallengeCardStyles;
  theme: Theme;
}

export const BeatTheMonkeyButton: React.FC<BeatTheMonkeyButtonProps> = ({ onPress, styles, theme }) => {
  const reducedMotion = useReducedMotion();
  const pop = useMonkeyPop(!reducedMotion);

  return (
    <View style={styles.monkeyButtonWrap}>
      <Pressable
        style={({ pressed, hovered }) => [
          styles.monkeyButton,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.monkeyButtonPressed,
          hovered && Platform.OS === 'web' && styles.monkeyButtonHover,
        ]}
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={HOME_COPY.SLATE_MONKEY_A11Y}
        testID={HOME_TEST_IDS.slateMonkey}
      >
        <LinearGradient colors={[theme.accent, theme.accentDark]} style={styles.monkeyGradient}>
          <Text style={styles.monkeyText}>{HOME_COPY.SLATE_MONKEY_LINE_1}</Text>
          <Text style={styles.monkeyText}>{HOME_COPY.SLATE_MONKEY_LINE_2}</Text>
        </LinearGradient>
      </Pressable>
      <Animated.View
        style={[
          styles.monkeyMascot,
          {
            // RN scales about the centre; the translate sandwich moves the pivot to the feet so the
            // squash presses into the button edge and the stretch rises from it, as in the design.
            transform: [
              { translateY: pop.translateY },
              { translateY: SLATE_MONKEY_SIZE / 2 },
              { scaleX: pop.scaleX },
              { scaleY: pop.scaleY },
              { translateY: -SLATE_MONKEY_SIZE / 2 },
            ],
          },
        ]}
        pointerEvents="none"
      >
        <AnimatedMonkey size={SLATE_MONKEY_SIZE} />
      </Animated.View>
    </View>
  );
};
